import { __decorate } from "tslib";
import '@cigna/cigna_csg_gsg_cipublic-icon';
import { html } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, queryAssignedNodes, state, query } from 'lit/decorators.js';
import { ElementVisibleMixin } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/element-visible.js';
import { gridBreakpoints } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/breakpoints.js';
import './tab-section.js';
import styles from './tabs.scss.js';
export class Tabs extends ElementVisibleMixin(CipublicElement) {
    constructor() {
        super(...arguments);
        this.variant = 'horizontal';
        this._isVisible = false;
        this.initialVertical = false;
        this.windowBackdrop = false;
    }
    get isMobile() {
        return !window.matchMedia(`(min-width: ${gridBreakpoints.lg}px)`).matches;
    }
    static get styles() {
        return [styles];
    }
    handleResize() {
        this.requestUpdate();
    }
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this.handleResize.bind(this));
        this.addEventListener('keydown', this.keyPress);
        this.addEventListener('tabs.click', this.tabClick);
    }
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        this.removeEventListener('keydown', this.keyPress);
        this.removeEventListener('tabs.click', this.tabClick);
        super.disconnectedCallback();
    }
    async firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        await this.updateComplete;
        const hash = window.location.hash.replace('#', '');
        let urlSections;
        if (hash) {
            urlSections = this.querySelectorAll(`:scope > [label='${hash}']`);
        }
        if (urlSections && urlSections.length > 0) {
            const childArray = Array.from(this.children);
            childArray.forEach((element) => {
                element.removeAttribute('active');
                element.removeAttribute('aria-selected');
                element.classList.remove('focus-visible');
            });
            urlSections.forEach((element) => {
                element.setAttribute('active', 'true');
                element.setAttribute('aria-selected', 'true');
                element.classList.add('focus-visible');
            });
            const activeTab = Array.from(urlSections).find((element) => element.tagName === 'CIPUBLIC-TAB');
            if (activeTab) {
                activeTab.scrollIntoView({ block: 'center', inline: 'center' });
            }
        }
        this.makeVerticalHorizontal();
        window.addEventListener('load', () => this.showArrow());
    }
    makeVerticalHorizontal() {
        if (this.variant === 'vertical' && this.isMobile) {
            this.initialVertical = true;
            this.variant = 'horizontal';
            this.allTabs.forEach((tab) => {
                tab.variant = 'horizontal';
            });
        }
        if (this.initialVertical && !this.isMobile) {
            this.variant = 'vertical';
            this.allTabs.forEach((tab) => {
                tab.variant = 'vertical';
            });
        }
    }
    update(changedProperties) {
        super.update(changedProperties);
        this.makeVerticalHorizontal();
        this.showArrow();
    }
    tabClick(event) {
        var _a;
        const { detail } = event;
        const target = detail ? this.querySelector(`cipublic-tab[label='${detail.label}']`) : event.target;
        if (target.parentElement === this) {
            const activeTab = this.querySelector(`cipublic-tab[active]`);
            const activeSection = this.querySelector(`cipublic-tab-section[active]`);
            activeTab === null || activeTab === void 0 ? void 0 : activeTab.removeAttribute('active');
            (_a = activeTab === null || activeTab === void 0 ? void 0 : activeTab.classList) === null || _a === void 0 ? void 0 : _a.remove('focus-visible');
            activeTab === null || activeTab === void 0 ? void 0 : activeTab.removeAttribute('aria-selected');
            activeSection === null || activeSection === void 0 ? void 0 : activeSection.removeAttribute('active');
            activeSection === null || activeSection === void 0 ? void 0 : activeSection.setAttribute('aria-hidden', 'true');
            const targetLabel = target.getAttribute('label');
            target.setAttribute('active', 'true');
            const targetSection = this.querySelector(`cipublic-tab-section[label='${targetLabel}']`);
            targetSection === null || targetSection === void 0 ? void 0 : targetSection.setAttribute('active', 'true');
            targetSection === null || targetSection === void 0 ? void 0 : targetSection.setAttribute('aria-hidden', 'false');
        }
    }
    keyPress(event) {
        var _a, _b, _c;
        const arrowArray = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.arrow');
        arrowArray.forEach((element) => {
            element.style.display = 'none';
        });
        const focusFunction = (event, isLeft) => {
            var _a;
            event.preventDefault();
            const allTabsLen = this.allTabs.length;
            const thisElement = event.target;
            const currentPosition = Array.from(this.allTabs).indexOf(thisElement);
            const previousPosition = this.allTabs[(currentPosition + allTabsLen - 1) % allTabsLen];
            const nextPosition = this.allTabs[(currentPosition + 1) % allTabsLen];
            const nextElement = isLeft ? previousPosition : nextPosition;
            if (nextElement && nextElement.nodeName == 'CIPUBLIC-TAB') {
                thisElement.classList.remove('focus-visible');
                const nextTab = (_a = nextElement.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.tab-item');
                nextTab.focus();
                nextElement.classList.add('focus-visible');
                nextTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                this.showArrow();
            }
        };
        switch (event.key) {
            case 'ArrowRight':
                focusFunction(event, false);
                break;
            case 'ArrowDown':
                if (!(this.variant === 'atoz' && this.isMobile) && event.target.nodeName === 'CIPUBLIC-TAB') {
                    event.target.classList.add('focus-visible');
                    ((_c = (_b = this.querySelector('cipublic-tab-section[active]')) === null || _b === void 0 ? void 0 : _b.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector('section')).focus();
                }
                break;
            case 'ArrowLeft':
                focusFunction(event, true);
                break;
            case 'Tab':
                event.target.classList.add('focus-visible');
                this.showArrow();
                break;
            default:
                this.showArrow();
                break;
        }
    }
    showArrow() {
        var _a, _b;
        if (this.variant !== 'horizontal' && this.variant !== 'text')
            return;
        const [leftArrow, rightArrow] = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.arrow');
        const tabsContainer = (_b = this.shadowRoot) === null || _b === void 0 ? void 0 : _b.querySelector('.tabs-container');
        if (leftArrow) {
            if (tabsContainer.scrollLeft === 0) {
                leftArrow.style.display = 'none';
            }
            else {
                leftArrow.style.display = 'block';
            }
        }
        if (rightArrow) {
            if (tabsContainer.scrollWidth > tabsContainer.clientWidth &&
                Math.ceil(tabsContainer.scrollLeft) < tabsContainer.scrollWidth - tabsContainer.clientWidth) {
                rightArrow.style.display = 'block';
            }
            else {
                rightArrow.style.display = 'none';
            }
        }
    }
    renderButton(isLeft) {
        if (this.variant === 'horizontal' || this.variant === 'text') {
            if (isLeft) {
                return html `<cipublic-button icononly size="small" type="button" variant="secondary" @click="${this.scrollTabs.bind(this, true)}" class="arrow arrow-left"><cipublic-icon set="system" name="chevron-right" class="rotate-180" role="img" aria-label="Scroll Left"></cipublic-icon></cipublic-button>`;
            }
            else {
                return html `<cipublic-button icononly size="small" type="button" variant="secondary" @click="${this.scrollTabs.bind(this, false)}" class="arrow arrow-right"><cipublic-icon set="system" name="chevron-right" role="img" aria-label="Scroll Right"></cipublic-icon></cipublic-button>`;
            }
        }
        return;
    }
    renderTabs() {
        var _a, _b, _c;
        if (this.variant === 'atoz' &&
            ((_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.tabs')) === null || _b === void 0 ? void 0 : _b.clientHeight) >
                ((_c = this.querySelector('cipublic-tab')) === null || _c === void 0 ? void 0 : _c.clientHeight) * 2 + 5) {
            return html `<div class="atoz-filter"><cipublic-button size="small" type="button" variant="secondary" @click="${this.toggleMobileSidebar}">A - Z<cipublic-icon set="system" name="bars" slot="suffix"></cipublic-icon></cipublic-button></div><div class="mobile-menu-wrapper"><div class="tabs-wrapper"><div class="close-row"><div class="a-z">A-Z</div><cipublic-close-button class="menu-toggle-button menu-close-button" @click="${this.toggleMobileSidebar}"></cipublic-close-button></div><div class="tabs-container"><ul class="tabs" id="tabset" role="tablist"><slot name="tab"></slot></ul></div></div></div><cipublic-backdrop ?active="${this.windowBackdrop}"></cipublic-backdrop>`;
        }
        else {
            return html `<div class="tabs-wrapper" id="test">${this.renderButton(true)}<div class="tabs-container"><ul class="tabs" id="tabset" role="tablist"><slot name="tab"></slot></ul></div>${this.renderButton(false)}</div>`;
        }
    }
    render() {
        return html `${this.renderTabs()}<div class="tab-content" id="tabset-content"><slot name="tab-section"></slot></div>`;
    }
    toggleMobileSidebar() {
        var _a;
        this.dispatchEvent(new CustomEvent('tabs.toggleSidebar', { bubbles: true, composed: true, cancelable: true }));
        this.windowBackdrop = !this.windowBackdrop;
        (_a = this.mobileMenu) === null || _a === void 0 ? void 0 : _a.classList.toggle('open');
    }
    scrollTabs(isLeft) {
        var _a, _b, _c, _d;
        this.dispatchEvent(new CustomEvent('tabs.scroll', { bubbles: true, composed: true, cancelable: true }));
        let scrollDistance;
        let target;
        const tabContainer = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.tabs-container');
        const tabContainerWidth = tabContainer.scrollWidth - tabContainer.clientWidth;
        const allTabsLen = this.allTabs.length;
        const currTab = this.querySelector('[scroll-target]');
        const currentPosition = Array.from(this.allTabs).indexOf(currTab);
        const previousPosition = this.allTabs[(currentPosition + allTabsLen - 1) % allTabsLen];
        const nextPosition = this.allTabs[(currentPosition + 1) % allTabsLen];
        const sibling = isLeft ? previousPosition : nextPosition;
        if (sibling) {
            target = isLeft ? sibling : currTab;
        }
        else {
            target = currTab;
        }
        const updateTabTarget = () => {
            sibling.setAttribute('scroll-target', '');
            currTab.removeAttribute('scroll-target');
        };
        const padding = parseFloat(window.getComputedStyle((_b = target.shadowRoot) === null || _b === void 0 ? void 0 : _b.querySelector('[data-toggle="tab"]')).getPropertyValue('padding-right'));
        if (isLeft) {
            const offsetLeft = tabContainer.scrollLeft > currTab.offsetLeft ? currTab.offsetLeft : sibling.offsetLeft;
            const firstTab = this.querySelector(`cipublic-tab`);
            const arrowWidth = ((_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector('.arrow-left')).clientWidth;
            const leftMargin = firstTab.offsetLeft === 0 ? arrowWidth + padding : firstTab.offsetLeft;
            scrollDistance = tabContainer.scrollLeft - offsetLeft + leftMargin;
            scrollDistance = -scrollDistance;
            if (sibling && tabContainer.scrollLeft < currTab.offsetLeft) {
                updateTabTarget();
            }
        }
        else {
            if (tabContainerWidth > tabContainer.scrollLeft + target.clientWidth) {
                updateTabTarget();
            }
            const offsetLeft = target.offsetLeft;
            const arrowWidth = ((_d = this.shadowRoot) === null || _d === void 0 ? void 0 : _d.querySelector('.arrow-right')).clientWidth;
            const leftMargin = arrowWidth + padding;
            scrollDistance = offsetLeft === 0 ? target.clientWidth - leftMargin : target.clientWidth;
        }
        tabContainer.scrollTo(tabContainer.scrollLeft + scrollDistance, 0);
        tabContainer.onscroll = () => {
            this.showArrow();
        };
    }
}
__decorate([
    property({ reflect: true })
], Tabs.prototype, "variant", void 0);
__decorate([
    state()
], Tabs.prototype, "_isVisible", void 0);
__decorate([
    state()
], Tabs.prototype, "initialVertical", void 0);
__decorate([
    state()
], Tabs.prototype, "isMobile", null);
__decorate([
    state()
], Tabs.prototype, "windowBackdrop", void 0);
__decorate([
    query('.mobile-menu-wrapper')
], Tabs.prototype, "mobileMenu", void 0);
__decorate([
    queryAssignedNodes('tab', true)
], Tabs.prototype, "allTabs", void 0);
//# sourceMappingURL=tabs.js.map